import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import QuizForm from "@/components/QuizForm";
import ContactSection from "@/components/ContactSection";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CookieBanner from "@/components/CookieBanner";
import { useLang } from "@/i18n";
import { captureAttribution } from "@/lib/attribution";
import { pushEvent } from "@/lib/analytics";
import {
  Award, ShieldCheck, MessageSquareHeart, Sparkles, MapPin, Phone, Mail,
  Plane, Mountain, Trophy, Users, Car, Star, ChevronRight, Instagram, Facebook,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=2400&q=80";
const FOOTER_IMG =
  "https://customer-assets.emergentagent.com/job_madeira-promo/artifacts/e9xbxqb2_IMG_20240719_145554.jpg";

const OFFER_ICONS = [Plane, Mountain, Trophy, Users, Car];
const OFFER_IMGS = [
  "https://customer-assets.emergentagent.com/job_madeira-promo/artifacts/etgw2i9x_Enotel%20Lido.jpg",
  "https://customer-assets.emergentagent.com/job_madeira-promo/artifacts/qyb3pxj5_371525083_1775755216231002_1735152260467894232_n.jpg",
  "https://customer-assets.emergentagent.com/job_madeira-promo/artifacts/oz4d0ac7_IMG_20240216_130712.jpg",
  "https://customer-assets.emergentagent.com/job_madeira-promo/artifacts/j0wi9q7g_2023-06-13%2013.39.54.jpg",
  "https://customer-assets.emergentagent.com/job_madeira-promo/artifacts/sb3noobh_ChatGPT%20Image%2026%20maj%202026%20o%2022_23_28.png",
];
const STRENGTH_ICONS = [ShieldCheck, Award, MessageSquareHeart];
const STAT_VALUES = [10, 100, 100, 48];

function Counter({ to, suffix = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start;
    const duration = 1400;
    const step = (tt) => {
      if (!start) start = tt;
      const p = Math.min(1, (tt - start) / duration);
      setN(Math.floor(to * (0.2 + 0.8 * (1 - Math.pow(1 - p, 3)))));
      if (p < 1) raf = requestAnimationFrame(step);
      else setN(to);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return <span>{n}{suffix}</span>;
}

export default function LandingPage() {
  const { t } = useLang();

  useEffect(() => { captureAttribution(); }, []);

  return (
    <div data-testid="landing-page" className="font-body bg-navy-deep text-cream">
      {/* NAV */}
      <header className="absolute top-0 inset-x-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold to-brass flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-navy-deep" />
            </div>
            <span className="font-display text-xl text-cream tracking-tight font-bold">Madeira.com.pl</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-cream/80">
            <a href="#oferta" className="hover:text-gold transition">{t.nav.offer}</a>
            <a href="#dlaczego" className="hover:text-gold transition">{t.nav.why}</a>
            <a href="#proces" className="hover:text-gold transition">{t.nav.process}</a>
            <a href="#faq" className="hover:text-gold transition">{t.nav.faq}</a>
            <a href="#kontakt" className="hover:text-gold transition">{t.nav.contact}</a>
          </nav>
          <div className="flex items-center gap-4">
            <LanguageSwitcher variant="dark" />
            <a href={`tel:${t.phone.replace(/\s/g, '')}`} onClick={() => pushEvent('phone_click', { source: 'nav' })} className="hidden sm:flex items-center gap-2 text-cream text-sm font-semibold">
              <Phone className="w-4 h-4" /> {t.phone}
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center pt-28 pb-16 overflow-hidden">
        <img src={HERO_IMG} alt="Madera" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="absolute inset-0 grain"></div>

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center w-full">
          <div className="text-cream animate-fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-navy-mid/60 backdrop-blur border border-gold/30 text-xs uppercase tracking-[0.2em] text-gold">
              <span className="w-1.5 h-1.5 rounded-full bg-gold"></span>
              {t.hero.tag}
            </span>
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-6 mb-6">
              {t.hero.title1}<br/>
              <span className="italic text-gold">{t.hero.title2}</span>.
            </h1>
            <p className="text-lg text-cream/80 max-w-xl mb-7">{t.hero.desc}</p>
            <div className="flex flex-wrap items-center gap-4">
              <a href="#quiz">
                <Button data-testid="hero-cta-btn" className="btn-primary px-7 py-6 text-base rounded-full">
                  {t.hero.cta} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </a>
              <a href="#oferta" className="btn-ghost-light px-6 py-3 rounded-full text-sm font-semibold">
                {t.hero.cta2}
              </a>
            </div>
            <div className="flex items-center gap-5 mt-10 text-cream/70 text-sm">
              <div className="flex -space-x-2">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-9 h-9 rounded-full border-2 border-navy-deep" style={{background:`linear-gradient(135deg, hsl(${i*60} 50% 60%), hsl(${i*60+30} 50% 45%))`}}></div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-0.5 text-gold">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-gold" />)}
                </div>
                <span>{t.hero.reviews}</span>
              </div>
            </div>
          </div>

          <div id="quiz" className="lg:pl-8 animate-fade-up delay-200">
            <QuizForm />
          </div>
        </div>
      </section>

      {/* USP BANNER */}
      <section className="bg-navy-deep text-cream py-5 border-y border-navy-line">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-2 text-sm">
          <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-gold"/> {t.usp[0]}</span>
          <span className="hidden sm:block w-1 h-1 rounded-full bg-cream/30"></span>
          <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold"/> {t.usp[1]}</span>
          <span className="hidden sm:block w-1 h-1 rounded-full bg-cream/30"></span>
          <span className="flex items-center gap-2"><MessageSquareHeart className="w-4 h-4 text-gold"/> {t.usp[2]}</span>
        </div>
      </section>

      {/* OFFER */}
      <section id="oferta" data-testid="offer-section" className="relative py-24 bg-navy">
        <div className="absolute inset-0 grain opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{t.offer.tag}</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl leading-tight mb-4">
              {t.offer.title1}<br/>
              <span className="italic text-gold">{t.offer.title2}</span>
            </h2>
            <p className="text-base text-cream/70">{t.offer.desc}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {t.offer.items.map((o, i) => {
              const Icon = OFFER_ICONS[i];
              return (
                <div key={o.title} data-testid={`offer-card-${i}`} className="card-lift bg-navy-mid rounded-2xl overflow-hidden border border-navy-line">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={OFFER_IMGS[i]} alt={o.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/60 to-transparent"></div>
                    <div className="absolute top-3 left-3 w-10 h-10 rounded-full bg-navy-deep/80 backdrop-blur border border-gold/40 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gold" />
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-widest text-gold mb-2">{o.price}</div>
                    <h3 className="font-display text-xl text-cream mb-2 leading-tight font-bold">{o.title}</h3>
                    <p className="text-sm text-cream/65 mb-4">{o.desc}</p>
                    <a href="#quiz" className="inline-flex items-center text-sm font-semibold text-gold hover:text-gold-soft transition">
                      {t.offer.cta} <ChevronRight className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section id="dlaczego" data-testid="why-section" className="relative py-24 bg-navy-deep">
        <div className="absolute inset-0 grain opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{t.why.tag}</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl leading-tight mb-4">
              {t.why.title1} <span className="italic text-gold">{t.why.title2}</span>
            </h2>
            <p className="text-base text-cream/70">{t.why.desc}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {t.why.items.map((s, i) => {
              const Icon = STRENGTH_ICONS[i];
              return (
                <div key={s.title} className="bg-navy rounded-2xl p-7 border border-navy-line card-lift">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gold to-brass text-navy-deep flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-display font-bold text-2xl text-cream mb-2">{s.title}</h3>
                  <p className="text-sm text-cream/65 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="proces" data-testid="process-section" className="relative py-24 bg-navy overflow-hidden">
        <div className="absolute inset-0 grain opacity-30"></div>
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{t.process.tag}</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl leading-tight mb-4">
              {t.process.title1}<br/>
              <span className="italic text-gold">{t.process.title2}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {t.process.steps.map((s, i) => (
              <div key={i} className="relative">
                <div className="text-7xl font-display font-bold text-gold/30 leading-none mb-4">0{i+1}</div>
                <h3 className="font-display font-bold text-2xl mb-2">{s.title}</h3>
                <p className="text-cream/70 text-sm leading-relaxed">{s.desc}</p>
                {i < 2 && (
                  <div className="hidden md:block absolute top-8 right-0 w-12 h-px bg-gold/30"></div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-14">
            <a href="#quiz">
              <Button data-testid="process-cta-btn" className="btn-primary px-8 py-6 text-base rounded-full">
                {t.process.cta} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="py-20 bg-navy-deep border-y border-navy-line">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {t.stats.map((st, i) => (
            <div key={i} data-testid={`stat-${i}`}>
              <div className="font-display font-bold text-5xl sm:text-6xl text-gold mb-2">
                <Counter to={STAT_VALUES[i]} suffix={st.suffix} />
              </div>
              <div className="text-xs uppercase tracking-widest text-cream/70">{st.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="py-24 bg-navy">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{t.faq.tag}</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl leading-tight">
              {t.faq.title1} <span className="italic text-gold">{t.faq.title2}</span>
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {t.faq.items.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-navy-mid rounded-xl px-5 border border-navy-line shadow-sm">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-semibold text-cream hover:text-gold hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-cream/70 pb-5 text-sm leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CONTACT */}
      <ContactSection />

      <CookieBanner />

      {/* FOOTER */}
      <footer data-testid="footer" className="relative bg-navy-deep text-cream/80">
        <div className="relative h-48 overflow-hidden border-t border-navy-line">
          <img src={FOOTER_IMG} alt="Madera" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/40 to-navy-deep"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold to-brass flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-navy-deep" />
              </div>
              <span className="font-display font-bold text-xl text-cream">Madeira.com.pl</span>
            </div>
            <p className="text-cream/55">{t.footer.desc}</p>
            <div className="mt-4"><LanguageSwitcher variant="dark" /></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-gold mb-3">{t.footer.tag1}</div>
            <ul className="space-y-2">
              <li className="flex items-center gap-2"><Phone className="w-4 h-4"/> {t.phone}</li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4"/> mail@madeira.com.pl</li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5"/> Rua da Bolivia 72-JS, Funchal</li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-gold mb-3">{t.footer.tag2}</div>
            <ul className="space-y-2">
              <li><a href="#oferta" className="hover:text-gold">{t.nav.offer}</a></li>
              <li><a href="#dlaczego" className="hover:text-gold">{t.nav.why}</a></li>
              <li><a href="#faq" className="hover:text-gold">{t.nav.faq}</a></li>
              <li><a href="#kontakt" className="hover:text-gold">{t.nav.contact}</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-gold mb-3">{t.footer.tag3}</div>
            <div className="flex gap-3 mb-4">
              <a href="https://www.facebook.com/people/Madeiracompl/61560192879367/" className="w-10 h-10 rounded-full border border-navy-line flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy-deep transition" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/madeira.com.pl/" className="w-10 h-10 rounded-full border border-navy-line flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy-deep transition" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
            <p className="text-xs text-cream/55">RNAVT n° 11932 / RNAAT n° 229/2026</p>
          </div>
        </div>
        <div className="border-t border-navy-line py-5 text-center text-xs text-cream/50">
          © {new Date().getFullYear()} Madeira.com.pl – {t.footer.rights}
        </div>
      </footer>
    </div>
  );
}
